import { baseService } from "../base";
import {
  EstimateTicket,
  CreateEstimateDto,
  UpdateEstimateDto,
  GetEstimatesParams,
  ConvertEstimateDto,
  EstimateService,
  CreateEstimateServiceDto,
  CreateExpoServiceDaysDto,
  GenerateExpoDaysDto,
  CopyExpoDayDto,
  ExpoServiceSummary,
  ExpoServiceDay,
} from "~/types/estimate";
import { TBooking } from "~/types/booking";
import { PaginationResponse } from "~/types/common";

export const estimateApi = {
  // Create a new estimate
  create: async (data: CreateEstimateDto): Promise<EstimateTicket> => {
    const response = await baseService.post<EstimateTicket>("/estimates", data);
    return response.data;
  },

  // Get all estimates with pagination and filters
  getAll: async (params: GetEstimatesParams): Promise<PaginationResponse<EstimateTicket>> => {
    const response = await baseService.get<PaginationResponse<EstimateTicket>>("/estimates", {
      params,
    });
    return response.data;
  },

  // Get estimate by ID
  getById: async (id: string): Promise<EstimateTicket> => {
    const response = await baseService.get<EstimateTicket>(`/estimates/${id}`);
    console.log('🌐 API getById response:', response.data);
    console.log('🌐 Services in response:', response.data.services);
    return response.data;
  },

  // Update estimate
  update: async (id: string, data: UpdateEstimateDto): Promise<EstimateTicket> => {
    const response = await baseService.put<EstimateTicket>(`/estimates/${id}`, data);
    return response.data;
  },

  // Delete estimate
  delete: async (id: string): Promise<void> => {
    await baseService.delete(`/estimates/${id}`);
  },

  // Convert estimate to booking
  convertToBooking: async (data: ConvertEstimateDto): Promise<TBooking> => {
    const response = await baseService.post<TBooking>("/estimates/convert", data);
    return response.data;
  },

  // Convert estimate to invoice
  convertToInvoice: async (estimateId: string): Promise<any> => {
    const response = await baseService.post(`/estimates/${estimateId}/convert-to-invoice`);
    return response.data;
  },

  // Service CRUD operations
  addService: async (data: CreateEstimateServiceDto): Promise<EstimateService> => {
    const response = await baseService.post<EstimateService>("/estimates/services", data);
    return response.data;
  },

  updateService: async (serviceId: string, data: Partial<CreateEstimateServiceDto>): Promise<EstimateService> => {
    const response = await baseService.put<EstimateService>(`/estimates/services/${serviceId}`, data);
    return response.data;
  },

  deleteService: async (serviceId: string): Promise<void> => {
    await baseService.delete(`/estimates/services/${serviceId}`);
  },

  // ============================================
  // EXPO MODE API METHODS
  // ============================================

  // Generate expo days from a date range
  generateExpoDays: async (data: GenerateExpoDaysDto): Promise<ExpoServiceDay[]> => {
    const response = await baseService.post<ExpoServiceDay[]>("/estimates/expo/generate-days", data);
    return response.data;
  },

  // Save expo days with positions
  saveExpoDays: async (data: CreateExpoServiceDaysDto): Promise<ExpoServiceSummary> => {
    const response = await baseService.post<ExpoServiceSummary>("/estimates/expo/days", data);
    return response.data;
  },

  // Get expo service summary
  getExpoServiceSummary: async (serviceId: string): Promise<ExpoServiceSummary> => {
    const response = await baseService.get<ExpoServiceSummary>(`/estimates/expo/services/${serviceId}/summary`);
    return response.data;
  },

  // Copy positions from one day to others
  copyExpoDay: async (data: CopyExpoDayDto): Promise<ExpoServiceSummary> => {
    const response = await baseService.post<ExpoServiceSummary>("/estimates/expo/copy-day", data);
    return response.data;
  },

  // Delete an expo day
  deleteExpoDay: async (dayId: string): Promise<void> => {
    await baseService.delete(`/estimates/expo/days/${dayId}`);
  },

  // Check if estimate is in expo mode
  isExpoMode: async (estimateId: string): Promise<{ isExpoMode: boolean }> => {
    const response = await baseService.get<{ isExpoMode: boolean }>(`/estimates/${estimateId}/is-expo-mode`);
    return response.data;
  },
};
